// #AI: Client-side filter for hiding voted elections after Turbo Stream broadcasts
// Turbo Streams broadcast ALL running elections to all clients (stateless)
// This controller filters the DOM client-side based on session keys (voted invitations)
//
// BFCache note: Chrome for Android (96+) ignores Cache-Control: no-store for BFCache.
// When the user returns via back-navigation, the page is restored from BFCache with
// stale votedKeysValue. The pageshow handler detects this and reloads the page so
// the server re-renders with up-to-date voted_election_ids from the session.

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    votedKeys: Array, // Array of voted election IDs
  };

  declare readonly votedKeysValue: number[];

  private _observer: MutationObserver | null = null;
  private _handlePageShow: ((event: PageTransitionEvent) => void) | null = null;

  // #AI: Filter on connect (initial load) and after Turbo Stream updates
  connect() {
    this.filterVotedElections();
    this.observeTurboStreamUpdates();
    this.handleBFCacheRestore();
  }

  // #AI: Clean up observer and pageshow listener to prevent memory leaks
  disconnect() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._handlePageShow) {
      window.removeEventListener("pageshow", this._handlePageShow);
      this._handlePageShow = null;
    }
  }

  // #AI: Hide election buttons where election ID matches voted election IDs
  filterVotedElections() {
    const votedElectionIds = this.votedKeysValue;

    const electionButtons = this.element.querySelectorAll(
      "[data-election-button]"
    );

    electionButtons.forEach((button) => {
      const htmlButton = button as HTMLElement;
      const electionId = htmlButton.dataset.electionId;
      if (
        electionId &&
        votedElectionIds.length > 0 &&
        votedElectionIds.includes(parseInt(electionId, 10))
      ) {
        htmlButton.style.display = "none"; // Hide voted election
      } else {
        htmlButton.style.display = ""; // Show unvoted election
      }
    });

    this.toggleEmptyState();
  }

  // #AI: Show empty state if all elections are hidden
  toggleEmptyState() {
    const electionButtons = this.element.querySelectorAll(
      "[data-election-button]"
    );
    const visibleButtons = Array.from(electionButtons).filter(
      (button) => (button as HTMLElement).style.display !== "none"
    );

    const emptyStateExists = this.element.querySelector(
      "[data-empty-state]"
    );

    if (visibleButtons.length === 0 && !emptyStateExists) {
      // All elections hidden, show empty state if not already present
      this.showEmptyState();
    } else if (visibleButtons.length > 0 && emptyStateExists) {
      // Some elections visible, hide empty state if present
      emptyStateExists.remove();
    }
  }

  // #AI: Insert empty state message when all elections are filtered out
  // ID matches EmptyStateComponent template for Turbo Stream `remove` compatibility
  showEmptyState() {
    const emptyStateHTML = `
      <div id="meetings_manager_empty_state" data-empty-state>
        When Meeting Votes are live, the buttons to access the voting process will appear here.
      </div>
    `;
    this.element.insertAdjacentHTML("beforeend", emptyStateHTML);
  }

  // #AI: Re-filter after Turbo Stream updates the DOM
  observeTurboStreamUpdates() {
    this._observer = new MutationObserver(() => {
      this.filterVotedElections();
    });

    this._observer.observe(this.element, {
      childList: true, // Watch for added/removed election buttons
      subtree: true,
    });
  }

  // #AI: Reload when BFCache restores a stale pre-vote snapshot.
  // Chrome for Android (96+) ignores Cache-Control: no-store for BFCache, so the
  // page can be restored with an outdated votedKeysValue=[] from a pre-vote render.
  // Forcing a reload ensures the server re-evaluates session[:authenticated_elections]
  // and renders the correct voted_election_ids before the ActionCable broadcast fires.
  handleBFCacheRestore() {
    this._handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", this._handlePageShow);
  }
}
